import { t } from '@lingui/core/macro';
import { ArrowLeft, Camera, Flashlight, FlashlightOff, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CAMERA_CONFIG } from '@/config/constants';
import { captureStreamPhoto } from '@/lib/capturePhoto';
import { BrowserPromptHint } from './BrowserPromptHint';
import { CaptureActionRail, CaptureRailSecondaryButton, CaptureShutterButton } from './CaptureActionRail';

export type InPageCameraErrorPhase = 'start' | 'shot';

interface InPageCameraProps {
  /** The photo, landscape and inside the capture box. GPS and upload happen after. */
  onShot: (photo: Blob) => void;
  onError: (error: unknown, phase: InPageCameraErrorPhase) => void;
  onBack: () => void;
}

type TorchCapabilities = MediaTrackCapabilities & { torch?: boolean };

/**
 * The camera inside the page. Chrome stays on screen while the photo is taken,
 * so Android has no background page to kill, unlike the native camera app.
 */
export function InPageCamera({ onShot, onError, onBack }: InPageCameraProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const [ready, setReady] = useState(false);
  const [buffer, setBuffer] = useState<'portrait' | 'landscape'>('landscape');
  const [shooting, setShooting] = useState(false);
  // null: this camera has no torch.
  const [torchOn, setTorchOn] = useState<boolean | null>(null);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let active = true;
    let stream: MediaStream | null = null;

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: CAMERA_CONFIG.FACING_MODE,
            width: { ideal: CAMERA_CONFIG.IDEAL_WIDTH },
            height: { ideal: CAMERA_CONFIG.IDEAL_HEIGHT },
          },
          audio: false,
        });
        // Unmounted while the camera was starting: cleanup had no stream to stop.
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const track = stream.getVideoTracks()[0];
        trackRef.current = track ?? null;
        if ((track?.getCapabilities?.() as TorchCapabilities | undefined)?.torch) setTorchOn(false);

        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        // The track turns with the device, so read it on every resize: with rotation
        // lock on it disagrees with (orientation: portrait) for the whole session.
        const readBuffer = () => {
          if (active) setBuffer(video.videoHeight > video.videoWidth ? 'portrait' : 'landscape');
        };
        video.onloadedmetadata = () => {
          if (!active) return;
          setReady(true);
          readBuffer();
        };
        video.onresize = readBuffer;
      } catch (error) {
        if (active) onErrorRef.current(error, 'start');
      }
    })();

    return () => {
      active = false;
      trackRef.current = null;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const shoot = async () => {
    const video = videoRef.current;
    if (!video || !ready || shooting) return;
    setShooting(true);
    try {
      onShot(await captureStreamPhoto(trackRef.current ?? undefined, video));
    } catch (error) {
      onErrorRef.current(error, 'shot');
    } finally {
      setShooting(false);
    }
  };

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track || torchOn === null) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn } as MediaTrackConstraintSet] });
      setTorchOn(!torchOn);
    } catch {
      setTorchOn(null);
    }
  };

  return (
    <div className="capture-overlay bg-black" data-testid="in-page-camera">
      <div className="capture-stage absolute inset-0 right-[100px] overflow-hidden">
        <video ref={videoRef} autoPlay playsInline muted data-buffer={buffer} className="capture-media-cover" />

        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-white text-center" role="status">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4" />
              <p>{t`Starting camera...`}</p>
            </div>
          </div>
        )}
      </div>

      <BrowserPromptHint waiting={!ready} />

      <CaptureActionRail className="absolute top-0 right-0 bottom-0">
        {torchOn !== null && (
          <CaptureRailSecondaryButton
            placement="top"
            onClick={toggleTorch}
            aria-pressed={torchOn}
            aria-label={torchOn ? t`Turn off flashlight` : t`Turn on flashlight`}
          >
            {torchOn ? <FlashlightOff className="w-6 h-6 text-white" /> : <Flashlight className="w-6 h-6 text-white" />}
          </CaptureRailSecondaryButton>
        )}

        <CaptureShutterButton tone="green" onClick={shoot} disabled={!ready || shooting} aria-label={t`Take photo`}>
          {shooting ? (
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          ) : (
            <Camera className="w-8 h-8 text-white" />
          )}
        </CaptureShutterButton>

        <CaptureRailSecondaryButton onClick={onBack} aria-label={t`Back`}>
          <ArrowLeft className="w-6 h-6 text-white" />
        </CaptureRailSecondaryButton>
      </CaptureActionRail>
    </div>
  );
}
